/**
 * Overlegpagina — /overleg/sessie/:id. Eén URL, drie standen op status van de sessie:
 * pakket (vooraf: cijfers, lijst, open acties, jouw invulblok), live (blokken met klok vanaf de
 * geplande start; besluiten en acties worden live ingevoerd) en recap (erna).
 * Werk-/detailpagina: eigen linkerrail met de sessies van dit overleg (geen secundaire nav).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, LinearProgress, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { useUserRole } from '../../contexts/UserRoleContext';
import { overlegApi } from './overlegApi';
import useOverlegSessie from './useOverlegSessie';
import IssueContextPaneel, { OpgelostDezeWeek } from './IssueContextPaneel';
import { issueContextApi } from './issueContextApi';
import { blokSchema, klokCorrectie, klokLoopt, klokStand, mmss, totStart } from './timer';
import {
  AanwezigheidChips, BesluitDialog, HeadlineLijst, IssueLijst, PrepFormulier, RatingDialog, RocksLijst, ScorecardStrook,
  SessieStatusChip, TodoLijst, TWEE_KOLOMMEN, bewaarActie, datumKort, naam, tijdKort,
} from './OverlegDelen';
import { VerzetDialoog, useVerzetten, vandaagNl } from './Verzetten';

const weekParity = (datum) => { const d = new Date(`${datum}T12:00:00`); const j1 = new Date(d.getFullYear(), 0, 4); const w = Math.round(((d - j1) / 86400000 + j1.getDay() + 6) / 7) + 1; return { week: w, even: w % 2 === 0 }; };
const variantHint = (blok, datum) => {
  if (!/even weken/i.test(blok?.vraag || '')) return null;
  const { week, even } = weekParity(datum);
  const m = (blok.vraag || '').match(/even weken:\s*([^.]+)\.\s*oneven weken:\s*([^.]+)/i);
  const tekst = m ? (even ? m[1] : m[2]).trim() : (even ? 'even week' : 'oneven week');
  return `Week ${week} is ${even ? 'even' : 'oneven'}: ${tekst}`;
};

const SOORT_LABEL = { cijfers: 'Cijfers', rocks: 'Rocks', headlines: 'Headlines', todos: 'Acties', acties: 'Acties', issues: 'Issues', besluiten: 'Besluiten', vrij: 'Bespreekpunt' };

// Module-niveau (niet inline): anders remount per render — zie MeetingDetailPage.
function OverlegRail({ sessies, activeId, typeNaam, onPick, onBack, vandaag }) {
  // Een sessie van vóór vandaag die nooit is afgesloten hoort niet bij 'Komend' (anders lijkt hij de eerstvolgende).
  const hangt = (s) => s.status !== 'afgesloten' && s.status !== 'overgeslagen' && vandaag && s.datum < vandaag;
  const klaar = (s) => s.status === 'afgesloten' || s.status === 'overgeslagen';
  return (
    <Box sx={{ width: 272, flexShrink: 0, display: { xs: 'none', lg: 'flex' }, flexDirection: 'column', borderRight: '1px solid #eceff1', bgcolor: '#fafbfc', px: 1.5, py: 2, overflowY: 'auto', height: 'calc(100vh - 44px)' }}>
      <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b', px: 0.5, mb: 1 }}>{typeNaam || 'Overleg'}</Typography>
      <Button size="small" onClick={onBack} sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 700, color: '#526075', mb: 1 }}>← Mijn week</Button>
      {[['Komend', sessies.filter((s) => !klaar(s) && !hangt(s)).sort((a, b) => a.datum.localeCompare(b.datum))],
        ['Niet afgesloten', sessies.filter(hangt).sort((a, b) => b.datum.localeCompare(a.datum))],
        ['Afgesloten', sessies.filter(klaar).sort((a, b) => b.datum.localeCompare(a.datum))]]
        .filter(([kop, lijst]) => kop !== 'Niet afgesloten' || lijst.length > 0).map(([kop, lijst]) => (
        <Box key={kop} sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', px: 1.25, mb: 0.5 }}>{kop}</Typography>
          <Stack spacing={0.25}>
            {lijst.map((s) => {
              const active = String(s.id) === String(activeId);
              return (
                <Box key={s.id} onClick={() => onPick(s.id)} sx={{ px: 1.25, py: 0.9, borderRadius: 1.5, cursor: 'pointer', borderLeft: '3px solid', borderColor: active ? '#526075' : 'transparent', bgcolor: active ? '#eef2f6' : 'transparent', '&:hover': { bgcolor: active ? '#eef2f6' : '#f3f4f6' } }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: active ? 700 : 600, color: '#374151' }}>{datumKort(s.datum)} · {tijdKort(s.gepland_start_nl)}</Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.4 }}>
                    <SessieStatusChip status={s.status} />
                    {s.rating_gem != null && <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>★ {s.rating_gem}</Typography>}
                  </Stack>
                </Box>
              );
            })}
            {lijst.length === 0 && <Typography sx={{ fontSize: 12, color: '#cbd5e1', px: 1.25 }}>—</Typography>}
          </Stack>
        </Box>
      ))}
    </Box>
  );
}

function Klok({ sessie, blokken, type, huidigBlokId, serverNow }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const corr = useMemo(() => klokCorrectie(serverNow), [serverNow]);
  const start = Date.parse(sessie.gepland_start_nl || sessie.gepland_start_at);
  const schema = useMemo(() => blokSchema(start, blokken), [start, blokken]);
  const k = klokStand(now + corr, schema, huidigBlokId, type.duur_min, start);
  const kleur = k.overTijdBlok ? '#b91c1c' : (k.resterendBlokSec != null && k.resterendBlokSec < 60 ? '#c2410c' : '#0f172a');
  const venster = `${tijdKort(sessie.gepland_start_nl)}–${tijdKort(new Date(start + type.duur_min * 60000).toISOString())}`;
  if (!k.gestart) {
    const nog = totStart(start - (now + corr));
    const kort = start - (now + corr) <= 5 * 60000;
    return (
      <Paper variant="outlined" sx={{ p: 1.5, minWidth: 200, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b' }}>start</Typography>
        <Typography sx={{ fontSize: kort ? 40 : 22, fontWeight: 800, lineHeight: 1.15, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{nog}</Typography>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>{datumKort(sessie.datum)} · {venster}</Typography>
      </Paper>
    );
  }
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 200, textAlign: 'center', borderColor: k.overTijdBlok ? '#fecaca' : '#e2e8f0', bgcolor: k.overTijdBlok ? '#fef2f2' : '#fff' }}>
      <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b' }}>{k.blok ? `dit blok · nog` : 'geen blok actief'}</Typography>
      <Typography sx={{ fontSize: 40, fontWeight: 800, lineHeight: 1.1, color: kleur, fontVariantNumeric: 'tabular-nums' }}>
        {k.blok ? mmss(k.resterendBlokSec) : '—'}
      </Typography>
      {k.blok && <LinearProgress variant="determinate" value={k.voortgangBlok * 100} sx={{ height: 4, borderRadius: 2, my: 0.75, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: k.overTijdBlok ? '#dc2626' : '#4338ca' } }} />}
      <Typography sx={{ fontSize: 12, color: k.overTijdTotaal ? '#b91c1c' : '#64748b', fontVariantNumeric: 'tabular-nums' }}>
        {k.overTijdTotaal ? `${mmss(-k.resterendTotaalSec)} over tijd` : `totaal nog ${mmss(k.resterendTotaalSec)}`} · {venster}
      </Typography>
    </Paper>
  );
}

export default function OverlegPagina() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const invulblokRef = useRef(null);
  const gescrold = useRef(false);
  const { permissions } = useUserRole();
  const { data, laden, fout, herlaad, actie, setData } = useOverlegSessie(id, { weken: 4 });
  const [personen, setPersonen] = useState([]);
  const [railSessies, setRailSessies] = useState([]);
  const [besluitOpen, setBesluitOpen] = useState(false);
  const [besluitIssue, setBesluitIssue] = useState(null);
  const [prepVelden, setPrepVelden] = useState(null);
  const [ratingLater, setRatingLater] = useState(false);
  const [mijnEmail, setMijnEmail] = useState('');
  // Issue-paneel (context: toelichting, mail, bijlagen, links). ?issue=<id> opent het meteen (link uit pakket/recap).
  const [issueOpen, setIssueOpen] = useState(() => Number(new URLSearchParams(location.search).get('issue')) || null);
  const [issueContext, setIssueContext] = useState({});
  const [issuesVersie, setIssuesVersie] = useState(0);

  useEffect(() => registreerOngedaanSneltoets(), []);
  useEffect(() => { overlegApi.personen().then(setPersonen).catch(() => {}); }, []);
  useEffect(() => {
    if (!data?.type?.id) return;
    overlegApi.sessies({ type_id: data.type.id }).then(setRailSessies).catch(() => {});
  }, [data?.type?.id, data?.sessie?.status]);
  useEffect(() => {
    if (data?.mijn_prep && prepVelden === null) setPrepVelden(data.mijn_prep.velden || {});
    if (data?.rating?.mijn_rating != null) setRatingLater(false);
  }, [data, prepVelden]);
  useEffect(() => {
    // eigen e-mail alleen voor UI-gemak (standaard eigenaar, ratingdialoog); rechten bepaalt de server.
    overlegApi.mijnWeek(true).then((d) => setMijnEmail(d?.mijn_email || '')).catch(() => {});
  }, []);

  // Vanuit Vandaag, het belletje of de mail: /overleg/sessie/:id#invulblok → meteen naar het invulblok.
  useEffect(() => {
    if (location.hash !== '#invulblok' || gescrold.current || !data || !invulblokRef.current) return;
    gescrold.current = true;
    invulblokRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash, data]);

  const issueSleutel = (data?.issues || []).map((i) => `${i.id}:${i.updated_at || ''}`).join(',');
  useEffect(() => {
    if (!data?.type?.id) return;
    issueContextApi.samenvatting(data.type.id).then((d) => setIssueContext(d?.tellingen || {})).catch(() => {});
  }, [data?.type?.id, issueSleutel, issuesVersie]);
  const openIssue = (issue) => setIssueOpen(issue.id);
  const sluitIssue = () => {
    setIssueOpen(null);
    if (new URLSearchParams(location.search).has('issue')) navigate(location.pathname + location.hash, { replace: true });
  };
  const issueGewijzigd = () => { herlaad(true); setIssuesVersie((v) => v + 1); };
  // Niet gehouden of overgeslagen: verzetten, overslaan, terugzetten (met ongedaan maken).
  const { verzet, overslaan } = useVerzetten(() => herlaad(true));
  const [verzetOpen, setVerzetOpen] = useState(false);
  const magSchrijven = !!(permissions?.overleg?.edit || permissions?.admin_settings?.edit);

  const s = data?.sessie;
  const t = data?.type;
  const magLeiden = !!t?.mag_leiden;
  const huidig = useMemo(() => (data?.blokken || []).find((b) => b.id === s?.huidig_blok_id) || null, [data, s]);

  const doe = useCallback(async (fn, tekst) => {
    try { const r = await fn(); if (tekst) toast.success(tekst); return r; } catch (e) { toast.error(e.message || 'Mislukt'); throw e; }
  }, []);

  const toggleTodo = async (todo) => {
    const nieuw = todo.status === 'gedaan' ? 'open' : 'gedaan';
    await doe(() => overlegApi.todoPatch(todo.id, { status: nieuw }));
    herlaad(true);
    biedOngedaanAan(nieuw === 'gedaan' ? 'Actie afgevinkt' : 'Actie heropend', async () => { await overlegApi.todoPatch(todo.id, { status: todo.status }); herlaad(true); }, { eigenKnop: true });
  };
  const bewerkTodo = (todo, wijziging) => bewaarActie(todo, wijziging, { mijnEmail, personen, patch: overlegApi.todoPatch, herlaad: () => herlaad(true) });
  const nieuweTodo = (body) => doe(() => overlegApi.todoPost({ ...body, type_id: t.id, sessie_id: s.id }), 'Actie toegevoegd').then(() => herlaad(true));
  const nieuwIssue = (body) => doe(() => overlegApi.issuePost({ ...body, type_id: t.id, sessie_id: s.id })).then(() => herlaad(true));
  const issueStatus = async (issue, status) => {
    if (status === 'opgelost') {
      const ok = await bevestig({ titel: 'Issue opgelost?', tekst: `"${issue.titel}" gaat van de lijst. Leg daarna het besluit en de acties vast.`, knop: 'Opgelost' });
      if (!ok) return;
    }
    const vorige = issue.status || 'open';
    try { await doe(() => overlegApi.issuePatch(issue.id, { status, sessie_id: s.id })); } catch { return; }
    issueGewijzigd();
    biedOngedaanAan(`Issue ${status}`, async () => { await overlegApi.issuePatch(issue.id, { status: vorige }); issueGewijzigd(); }, { eigenKnop: true });
    if (status === 'opgelost') { setBesluitIssue(issue); setBesluitOpen(true); }
  };
  const issueOmhoog = async (issue) => {
    const ids = (data.issues || []).map((i) => i.id);
    const idx = ids.indexOf(issue.id);
    if (idx <= 0) return;
    [ids[idx - 1], ids[idx]] = [ids[idx], ids[idx - 1]];
    await doe(() => overlegApi.issuesVolgorde(t.id, ids));
    herlaad(true);
  };
  const nieuwBesluit = (body) => doe(() => overlegApi.besluitPost({ ...body, type_id: t.id, sessie_id: s.id }), 'Besluit vastgelegd').then(() => herlaad(true));
  const nieuweHeadline = (body) => doe(() => overlegApi.headlinePost({ ...body, type_id: t.id, sessie_id: s.id })).then(() => herlaad(true));
  const verwijderHeadline = async (h) => {
    if (!(await bevestig({ titel: 'Headline verwijderen?', tekst: h.tekst, knop: 'Verwijderen', gevaarlijk: true }))) return;
    await doe(() => overlegApi.headlineDelete(h.id)); herlaad(true);
  };
  const zetWaarde = (m, periode, waarde) => doe(() => overlegApi.waardePut(m.id, periode, waarde === '' ? null : Number(waarde))).then(() => herlaad(true));

  const start = async () => {
    const startMs = Date.parse(s.gepland_start_nl || s.gepland_start_at);
    const minutenVooraf = Math.round((startMs - Date.now()) / 60000);
    if (minutenVooraf > 15) {
      const ok = await bevestig({ titel: 'Nu al starten?', tekst: `Het overleg begint pas om ${tijdKort(s.gepland_start_nl)} (over ${minutenVooraf > 90 ? `${Math.round(minutenVooraf / 60)} uur` : `${minutenVooraf} min`}). Tot die tijd is dit de voorbereiding.`, knop: 'Toch starten' });
      if (!ok) return;
    }
    await doe(() => actie('start'), minutenVooraf > 0 ? 'Overleg gestart' : 'Overleg gestart; de klok liep al vanaf de geplande tijd');
  };
  const terugNaarVoorbereiding = async () => {
    if (!(await bevestig({ titel: 'Terug naar voorbereiding?', tekst: 'De status gaat terug naar Voorbereiding. Acties, issues en besluiten die je al invoerde blijven staan.', knop: 'Terugzetten' }))) return;
    await doe(() => actie('voorbereiding'), 'Terug naar voorbereiding');
  };
  const kiesBlok = (b) => doe(() => actie('blok', { blok_id: b.id }));
  const volgendBlok = () => {
    const lijst = data?.blokken || [];
    const idx = lijst.findIndex((b) => b.id === s?.huidig_blok_id);
    const nxt = lijst[idx + 1];
    if (nxt) return kiesBlok(nxt);
    return afronden();
  };
  const afronden = async () => {
    if (!(await bevestig({ titel: 'Overleg afronden?', tekst: 'Iedereen krijgt de vraag om een cijfer te geven. Daarna sluit je af en gaat de recap-mail.', knop: 'Afronden' }))) return;
    await doe(() => actie('afronden'));
  };
  const afsluiten = async (forceer = false) => {
    try {
      const d = await actie('afsluiten', { forceer });
      toast.success(d?.recap === 'sent' ? 'Afgesloten, recap verstuurd' : `Afgesloten (recap: ${d?.recap || 'in de wachtrij'})`);
    } catch (e) {
      if (e.status === 409 && e.detail?.ontbrekend) {
        const ok = await bevestig({ titel: `${e.detail.ontbrekend} aanwezige(n) hebben nog niet beoordeeld`, tekst: `Wacht nog even, of sluit af zonder hun cijfer (kan na ${Math.round(e.detail.wacht_sec / 60)} min).`, knop: 'Afsluiten zonder' });
        if (ok) { try { await actie('afsluiten', { forceer: true }); toast.success('Afgesloten'); } catch (e2) { toast.error(e2.message); } }
      } else toast.error(e.message || 'Afsluiten mislukt');
    }
  };
  const sluitHangendeSessieAf = async () => {
    const ok = await bevestig({
      titel: `Sessie van ${datumKort(s.datum)} afsluiten?`,
      tekst: 'Deze sessie is nooit afgesloten. Besluiten en acties blijven staan; wie nog geen cijfer gaf, kan dat later nog. De recap gaat naar de deelnemers.',
      knop: 'Afsluiten',
    });
    if (!ok) return;
    try { await actie('afsluiten', { forceer: true }); toast.success('Sessie afgesloten'); } catch (e) { toast.error(e.message || 'Afsluiten mislukt'); }
  };
  const geefRating = async (cijfer, opmerking) => {
    await doe(() => overlegApi.rating(s.id, cijfer, opmerking), 'Dank, je cijfer is anoniem verwerkt');
    herlaad(true);
  };
  const dienPrepIn = async () => {
    const p = await doe(() => overlegApi.prepPut(s.id, prepVelden || {}, true), 'Invulblok ingediend');
    setData((d) => (d ? { ...d, mijn_prep: p, prep: [...(d.prep || []).filter((x) => x.email !== p.email), p] } : d));
  };

  if (laden && !data) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!data) return <Box sx={{ p: 4 }}><Alert severity="warning">{fout || 'Overleg niet gevonden'}</Alert><Button onClick={() => navigate('/overleg')} sx={{ mt: 2, textTransform: 'none' }}>← Mijn week</Button></Box>;

  const stand = s.status === 'afgesloten' ? 'recap' : (s.status === 'bezig' || s.status === 'beoordelen' ? 'live' : 'pakket');
  const nieuweActies = (data.todos || []).filter((x) => x.sessie_id === s.id);
  const toonRating = s.status === 'beoordelen' && data.rating?.mijn_rating == null && !ratingLater && (data.aanwezigheid?.[mijnEmail] !== 'afwezig');

  const blokInhoud = (b) => {
    const soort = b?.soort || 'vrij';
    if (soort === 'cijfers') return <ScorecardStrook measurables={data.scorecard} onWaarde={zetWaarde} magInvullen bijwerken={!!data.herberekening?.bezig} bijwerkenSinds={data.herberekening?.gestart_at} />;
    if (soort === 'rocks') return <RocksLijst rocks={data.rocks} magStrategie={!!permissions?.strategy?.edit} />;
    if (soort === 'headlines') return <HeadlineLijst headlines={data.headlines} onNieuw={nieuweHeadline} onVerwijder={verwijderHeadline} magSchrijven />;
    if (soort === 'issues') {
      return (
        <>
          <IssueLijst issues={data.issues} personen={personen} onNieuw={nieuwIssue} onStatus={issueStatus} onOmhoog={issueOmhoog} onOpen={openIssue} context={issueContext} mijnEmail={mijnEmail} />
          <OpgelostDezeWeek typeId={t.id} verversSleutel={issuesVersie} magSchrijven={magSchrijven} onOpen={openIssue} onGewijzigd={issueGewijzigd} />
        </>
      );
    }
    if (soort === 'besluiten') {
      return (
        <Stack spacing={1}>
          {(data.besluiten || []).map((bs) => (
            <Paper key={bs.id} variant="outlined" sx={{ p: 1.25, borderLeft: '4px solid #4338ca' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{bs.titel}</Typography>
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>{naam(bs.eigenaar_email)} · {bs.soort}{bs.rationale ? ` · ${bs.rationale}` : ''}</Typography>
            </Paper>
          ))}
          {(data.besluiten || []).length === 0 && <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Nog geen besluiten in dit overleg.</Typography>}
          <Button variant="contained" disableElevation onClick={() => { setBesluitIssue(null); setBesluitOpen(true); }} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}>＋ Besluit vastleggen</Button>
        </Stack>
      );
    }
    if (soort === 'todos' || soort === 'acties') return <TodoLijst todos={data.todos} personen={personen} onToggle={toggleTodo} onBewerk={bewerkTodo} onNieuw={nieuweTodo} mijnEmail={mijnEmail} standaardEigenaar={t.voorzitter_email} compact />;
    return (
      <Stack spacing={1.5}>
        {data.prep?.length > 0 && (
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', mb: 0.5 }}>Uit de invulblokken</Typography>
            <Stack spacing={0.75}>
              {data.prep.map((p) => (
                <Paper key={p.email} variant="outlined" sx={{ p: 1.25 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, mb: 0.5 }}>{naam(p.email)}</Typography>
                  <PrepFormulier schema={t.prep_schema} waarde={p.velden} onChange={() => {}} alleenLezen personen={personen} />
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
        <TodoLijst todos={data.todos} personen={personen} onToggle={toggleTodo} onBewerk={bewerkTodo} onNieuw={nieuweTodo} mijnEmail={mijnEmail} standaardEigenaar={t.voorzitter_email} compact />
      </Stack>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 44px)' }}>
      <OverlegRail sessies={railSessies} activeId={s.id} typeNaam={t.naam} vandaag={data.vandaag} onPick={(sid) => navigate(`/overleg/sessie/${sid}`)} onBack={() => navigate('/overleg')} />
      <Box sx={{ flex: 1, minWidth: 0, px: { xs: 2, md: 4 }, py: 3, maxWidth: 1400 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }} sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.01em' }}>{t.naam}</Typography>
              <SessieStatusChip status={s.status} />
              {t.vertrouwelijk && <Chip size="small" label="vertrouwelijk" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }} />}
            </Stack>
            <Typography sx={{ fontSize: 14, color: '#64748b' }}>
              {datumKort(s.datum)} · {tijdKort(s.gepland_start_nl)} · {t.duur_min} min · voorzitter {naam(t.voorzitter_email || t.eigenaar_email)}
            </Typography>
            <Box sx={{ mt: 1 }}>
              {stand === 'pakket' && t.prep_schema ? (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography sx={{ fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', mr: 0.5 }}>Voorbereid</Typography>
                  {(t.deelnemers_alle || []).map((e) => (
                    <Tooltip key={e} title={data.prep_status?.[e] ? 'Invulblok ingediend' : 'Invulblok nog niet ingevuld'}>
                      <Chip size="small" icon={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: data.prep_status?.[e] ? '#16a34a' : '#cbd5e1', ml: '6px !important' }} />} label={naam(e)}
                        sx={{ height: 24, fontWeight: 700, bgcolor: data.prep_status?.[e] ? '#dcfce7' : '#f1f5f9', color: data.prep_status?.[e] ? '#166534' : '#64748b' }} />
                    </Tooltip>
                  ))}
                </Stack>
              ) : (
                <AanwezigheidChips deelnemers={t.deelnemers_alle} aanwezigheid={data.aanwezigheid} magLeiden={magLeiden && stand === 'live'} onZet={(email, status) => doe(() => actie('aanwezigheid', { email, status }))} />
              )}
            </Box>
          </Box>
          {stand !== 'recap' && klokLoopt(s, data.vandaag) && <Klok sessie={s} blokken={data.blokken} type={t} huidigBlokId={s.huidig_blok_id} serverNow={data.server_now} />}
        </Stack>

        {data.verouderd && (
          <Alert severity="info" sx={{ mb: 2 }} action={(
            <Stack direction="row" spacing={1}>
              {magLeiden && (s.status === 'gepland' || s.status === 'pakket_verstuurd') && (
                <Button size="small" color="inherit" onClick={() => verzet({ ...s, type_naam: t.naam }, vandaagNl(), '')} sx={{ textTransform: 'none', fontWeight: 700 }}>Vandaag houden</Button>
              )}
              {magLeiden && (s.status === 'gepland' || s.status === 'pakket_verstuurd') && (
                <Button size="small" color="inherit" onClick={() => setVerzetOpen(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>Verzetten…</Button>
              )}
              {magLeiden && (s.status === 'gepland' || s.status === 'pakket_verstuurd') && (
                <Button size="small" color="inherit" onClick={() => overslaan({ ...s, type_naam: t.naam })} sx={{ textTransform: 'none', fontWeight: 700 }}>Overslaan</Button>
              )}
              {magLeiden && (s.status === 'bezig' || s.status === 'beoordelen') && (
                <Button size="small" color="inherit" onClick={sluitHangendeSessieAf} sx={{ textTransform: 'none', fontWeight: 700 }}>Afsluiten</Button>
              )}
              {data.volgende_sessie && (
                <Button size="small" color="inherit" onClick={() => navigate(`/overleg/sessie/${data.volgende_sessie.id}`)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Eerstvolgende ({datumKort(data.volgende_sessie.datum)}) →
                </Button>
              )}
            </Stack>
          )}>
            {s.status === 'gepland' || s.status === 'pakket_verstuurd'
              ? `Dit overleg van ${datumKort(s.datum)} is niet gehouden.${magLeiden ? ' Houd het alsnog, verzet het of sla het over.' : ''}`
              : `Deze sessie van ${datumKort(s.datum)} is nooit afgesloten.`}
          </Alert>
        )}

        {s.status === 'overgeslagen' && (
          <Alert severity="info" sx={{ mb: 2 }} action={magLeiden && (
            <Button size="small" color="inherit" onClick={() => doe(() => overlegApi.live(s.id, 'terugzetten', { status: 'gepland' }), 'Teruggezet').then(() => herlaad(true)).catch(() => {})} sx={{ textTransform: 'none', fontWeight: 700 }}>Terugzetten</Button>
          )}>
            Dit overleg van {datumKort(s.datum)} is overgeslagen.
          </Alert>
        )}
        {verzetOpen && <VerzetDialoog sessie={{ ...s, type_naam: t.naam }} onClose={() => setVerzetOpen(false)} onVerzet={verzet} />}

        {fout && <Alert severity="warning" sx={{ mb: 2 }}>{fout}</Alert>}

        {/* ── PAKKET ── */}
        {stand === 'pakket' && (
          <Stack spacing={2.5}>
            <Paper variant="outlined" sx={{ p: 2, borderColor: '#c7d2fe' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 800 }}>Agenda met klok · {t.duur_min} min</Typography>
                {magLeiden && !data.verouderd && s.status !== 'overgeslagen' && <Button variant="contained" disableElevation onClick={start} sx={{ textTransform: 'none', fontWeight: 700 }}>▶ Start overleg</Button>}
              </Stack>
              <Stack spacing={0.5}>
                {(data.blokken || []).map((b, i) => (
                  <Stack key={b.id} direction="row" spacing={1.5} alignItems="baseline">
                    <Typography sx={{ width: 96, fontSize: 12.5, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{tijdKort(b.start_gepland)}–{tijdKort(b.eind_gepland)}</Typography>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, width: 220 }}>{i + 1}. {b.naam} <Box component="span" sx={{ color: '#94a3b8', fontWeight: 500 }}>{b.minuten} min</Box></Typography>
                    <Typography sx={{ fontSize: 13, color: '#475569' }}>{b.vraag}{variantHint(b, s.datum) && <Box component="span" sx={{ ml: 1, px: 0.75, py: 0.1, borderRadius: 1, bgcolor: '#eef2ff', color: '#3730a3', fontSize: 12, fontWeight: 700 }}>{variantHint(b, s.datum)}</Box>}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 800 }}>Cijfers</Typography>
                {magLeiden && <Button size="small" disabled={!!data.herberekening?.bezig} onClick={() => doe(() => overlegApi.bereken(t.id, s.periode_datum || s.datum)).then(() => herlaad(true))} sx={{ textTransform: 'none', fontWeight: 700 }}>Ververs berekende cijfers</Button>}
              </Stack>
              <ScorecardStrook measurables={data.scorecard} onWaarde={zetWaarde} magInvullen bijwerken={!!data.herberekening?.bezig} bijwerkenSinds={data.herberekening?.gestart_at} />
            </Paper>
            <Box sx={{ display: 'grid', gridTemplateColumns: TWEE_KOLOMMEN, gap: 2 }}>
              <Paper variant="outlined" sx={{ p: 2 }}><Typography sx={{ fontWeight: 800, mb: 1 }}>Open acties ({(data.todos || []).filter((x) => x.status === 'open').length})</Typography><TodoLijst todos={(data.todos || []).filter((x) => x.status === 'open')} personen={personen} onToggle={toggleTodo} onBewerk={bewerkTodo} onNieuw={nieuweTodo} mijnEmail={mijnEmail} compact /></Paper>
              <Paper variant="outlined" sx={{ p: 2 }}><Typography sx={{ fontWeight: 800, mb: 1 }}>Issues op de lijst (top 5)</Typography>
                <IssueLijst issues={(data.issues || []).slice(0, 5)} personen={personen} onNieuw={nieuwIssue} onStatus={magSchrijven ? issueStatus : undefined}
                  onOmhoog={magSchrijven ? issueOmhoog : undefined} onOpen={openIssue} context={issueContext} mijnEmail={mijnEmail} />
                <OpgelostDezeWeek typeId={t.id} verversSleutel={issuesVersie} magSchrijven={magSchrijven} onOpen={openIssue} onGewijzigd={issueGewijzigd} />
              </Paper>
            </Box>
            {(data.blokken || []).some((b) => b.soort === 'rocks') && <Paper variant="outlined" sx={{ p: 2 }}><Typography sx={{ fontWeight: 800, mb: 1 }}>Rocks</Typography><RocksLijst rocks={data.rocks} magStrategie={!!permissions?.strategy?.edit} /></Paper>}
            {(data.blokken || []).some((b) => b.soort === 'besluiten') && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 800 }}>Besluiten</Typography>
                  <Button size="small" variant="outlined" onClick={() => { setBesluitIssue(null); setBesluitOpen(true); }} sx={{ textTransform: 'none', fontWeight: 700 }}>＋ Besluit vastleggen</Button>
                </Stack>
                {(data.prep || []).some((p) => (p.velden?.besluiten || []).length) && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', mb: 0.5 }}>Op tafel (uit de invulblokken)</Typography>
                    <Stack spacing={0.5}>
                      {(data.prep || []).flatMap((p) => (p.velden?.besluiten || []).map((b, i) => (
                        <Box key={`${p.email}-${i}`} sx={{ pl: 1.25, borderLeft: '3px solid #c7d2fe' }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{b.vraag || '(geen vraag)'} <Box component="span" sx={{ fontWeight: 500, color: '#94a3b8' }}>· {naam(p.email)}</Box></Typography>
                          {(b.opties || b.advies) && <Typography sx={{ fontSize: 12.5, color: '#475569' }}>{b.opties ? `Opties: ${b.opties}. ` : ''}{b.advies ? `Advies: ${b.advies}` : ''}</Typography>}
                        </Box>
                      )))}
                    </Stack>
                  </Box>
                )}
                <Stack spacing={0.75}>
                  {(data.besluiten_recent || []).slice(0, 5).map((bs) => (
                    <Box key={bs.id} sx={{ pl: 1.25, borderLeft: '3px solid #4338ca' }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{bs.titel}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748b' }}>{naam(bs.eigenaar_email)} · {bs.soort} · {datumKort(bs.created_at)}</Typography>
                    </Box>
                  ))}
                  {(data.besluiten_recent || []).length === 0 && <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Nog geen besluiten in dit overleg. Eén zin, één eigenaar, omkeerbaar of niet.</Typography>}
                </Stack>
              </Paper>
            )}
            {(data.blokken || []).some((b) => b.soort === 'headlines') && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 800, mb: 0.25 }}>{(data.blokken || []).find((b) => b.soort === 'headlines')?.naam || 'Headlines'}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Kort goed en slecht nieuws van klanten en team; vooraf invullen mag.</Typography>
                <HeadlineLijst headlines={data.headlines} onNieuw={nieuweHeadline} onVerwijder={verwijderHeadline} magSchrijven />
              </Paper>
            )}
            {t.prep_schema && (
              <Paper id="invulblok" ref={invulblokRef} variant="outlined" sx={{ p: 2, borderColor: '#c7d2fe', bgcolor: '#fafaff', scrollMarginTop: 64 }}>
                <Typography sx={{ fontWeight: 800 }}>Jouw invulblok</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Twee minuten. Vul in vóór het overleg; wie het pakket niet las, luistert.</Typography>
                <PrepFormulier schema={t.prep_schema} waarde={prepVelden || {}} onChange={setPrepVelden} onIndienen={dienPrepIn} personen={personen} ingediend={data.mijn_prep?.ingediend_at} />
              </Paper>
            )}
            {(data.prep || []).filter((p) => p.ingediend_at && p.email !== mijnEmail).length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 800, mb: 1 }}>Invulblokken van de anderen</Typography>
                <Stack spacing={0.75}>
                  {(data.prep || []).filter((p) => p.ingediend_at && p.email !== mijnEmail).map((p) => (
                    <Paper key={p.email} variant="outlined" sx={{ p: 1.25 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 800, mb: 0.5 }}>{naam(p.email)} <Box component="span" sx={{ fontWeight: 500, color: '#94a3b8' }}>· {datumKort(p.ingediend_at)} {tijdKort(p.ingediend_at)}</Box></Typography>
                      <PrepFormulier schema={t.prep_schema} waarde={p.velden} onChange={() => {}} alleenLezen personen={personen} />
                    </Paper>
                  ))}
                </Stack>
              </Paper>
            )}
          </Stack>
        )}

        {/* ── LIVE ── */}
        {stand === 'live' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '280px minmax(0, 1fr)' }, gap: 2 }}>
            <Stack spacing={0.75}>
              {(data.blokken || []).map((b, i) => {
                const actief = b.id === s.huidig_blok_id;
                const voorbij = huidig && b.volgorde < huidig.volgorde;
                return (
                  <Paper key={b.id} variant="outlined" onClick={magLeiden ? () => kiesBlok(b) : undefined}
                    sx={{ p: 1.25, cursor: magLeiden ? 'pointer' : 'default', borderColor: actief ? '#4338ca' : '#e2e8f0', bgcolor: actief ? '#eef2ff' : (voorbij ? '#f8fafc' : '#fff'), opacity: voorbij ? 0.7 : 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                      <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: actief ? '#3730a3' : '#1e293b' }}>{i + 1}. {b.naam}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{b.minuten} min</Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{tijdKort(b.start_gepland)}–{tijdKort(b.eind_gepland)} · {SOORT_LABEL[b.soort] || b.soort}</Typography>
                  </Paper>
                );
              })}
              {magLeiden && s.status === 'bezig' && (
                <Button variant="contained" disableElevation onClick={volgendBlok} sx={{ textTransform: 'none', fontWeight: 800, py: 1 }}>
                  {(() => { const l = data.blokken || []; const i = l.findIndex((b) => b.id === s.huidig_blok_id); return l[i + 1] ? `Volgende: ${l[i + 1].naam} →` : 'Laatste blok · Afronden'; })()}
                </Button>
              )}
              <Stack direction="row" spacing={1} sx={{ pt: 1 }} flexWrap="wrap" useFlexGap>
                {magLeiden && s.status === 'bezig' && Date.parse(s.gepland_start_nl || s.gepland_start_at) - Date.now() > 15 * 60000 && (
                  <Tooltip title="Per ongeluk te vroeg gestart? Zet terug; ingevoerde acties en besluiten blijven staan."><Button size="small" color="inherit" onClick={terugNaarVoorbereiding} sx={{ textTransform: 'none', fontWeight: 700, color: '#94a3b8' }}>← Terug naar voorbereiding</Button></Tooltip>
                )}
                <Tooltip title="Anoniem: 'we dwalen af'. Telt op, alleen de teller is zichtbaar."><Button size="small" variant="outlined" color="inherit" onClick={() => doe(() => actie('tangent'))} sx={{ textTransform: 'none', fontWeight: 700 }}>↩ Tangent{s.tangent_count ? ` (${s.tangent_count})` : ''}</Button></Tooltip>
                {magLeiden && s.status === 'bezig' && <Button size="small" variant="contained" disableElevation color="warning" onClick={afronden} sx={{ textTransform: 'none', fontWeight: 700 }}>Afronden</Button>}
                {magLeiden && s.status === 'beoordelen' && <Button size="small" variant="contained" disableElevation onClick={() => afsluiten(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>Afsluiten{data.rating?.nog_te_beoordelen ? ` (${data.rating.nog_te_beoordelen} nog)` : ''}</Button>}
              </Stack>
            </Stack>
            <Stack spacing={2}>
              {s.status === 'beoordelen' && (
                <Alert severity="info">Overleg afgerond. {data.rating?.nog_te_beoordelen ? `${data.rating.nog_te_beoordelen} aanwezige(n) beoordelen nog.` : 'Iedereen heeft beoordeeld.'} {data.rating?.mijn_rating == null && <Button size="small" onClick={() => setRatingLater(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>Geef je cijfer</Button>}</Alert>
              )}
              <Paper variant="outlined" sx={{ p: 2, minHeight: 320 }}>
                {huidig ? (
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b' }}>{SOORT_LABEL[huidig.soort] || 'Blok'}</Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, mb: 0.25 }}>{huidig.naam}</Typography>
                    {huidig.vraag && <Typography sx={{ fontSize: 13.5, color: '#475569', mb: 1.5 }}>{huidig.vraag}{variantHint(huidig, s.datum) && <Box component="span" sx={{ ml: 1, px: 0.75, py: 0.1, borderRadius: 1, bgcolor: '#eef2ff', color: '#3730a3', fontSize: 12, fontWeight: 700 }}>{variantHint(huidig, s.datum)}</Box>}</Typography>}
                    {blokInhoud(huidig)}
                  </Box>
                ) : <Typography sx={{ color: '#94a3b8' }}>Kies links een blok.</Typography>}
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 800, mb: 1 }}>Acties uit dit overleg ({nieuweActies.length})</Typography>
                <TodoLijst todos={nieuweActies} personen={personen} onToggle={toggleTodo} onBewerk={bewerkTodo} onNieuw={nieuweTodo} mijnEmail={mijnEmail} standaardEigenaar={t.voorzitter_email} compact />
              </Paper>
              {magLeiden && (
                <TextField label="Notities voorzitter" multiline minRows={2} size="small" defaultValue={s.notities || ''} onBlur={(e) => { if (e.target.value !== (s.notities || '')) doe(() => actie('notities', { notities: e.target.value })); }} />
              )}
            </Stack>
          </Box>
        )}

        {/* ── RECAP ── */}
        {stand === 'recap' && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc' }}>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <Box><Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: '#64748b' }}>Beoordeling</Typography><Typography sx={{ fontSize: 26, fontWeight: 800 }}>{data.rating?.rating_gem ?? '–'}</Typography><Typography sx={{ fontSize: 12, color: '#64748b' }}>{data.rating?.rating_n || 0} stemmen{data.rating?.rating_min != null ? ` · ${data.rating.rating_min}–${data.rating.rating_max}` : ''}</Typography></Box>
                <Box><Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: '#64748b' }}>Duur</Typography><Typography sx={{ fontSize: 26, fontWeight: 800 }}>{s.start_feitelijk_at && s.eind_feitelijk_at ? Math.round((Date.parse(s.eind_feitelijk_at) - Date.parse(s.start_feitelijk_at)) / 60000) : '–'} min</Typography><Typography sx={{ fontSize: 12, color: '#64748b' }}>gepland {t.duur_min} min · tangent {s.tangent_count || 0}×</Typography></Box>
                <Box><Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: '#64748b' }}>Aanwezig</Typography><Typography sx={{ fontSize: 13, mt: 1 }}>{Object.entries(data.aanwezigheid || {}).filter(([, v]) => v !== 'afwezig').map(([e]) => naam(e)).join(', ') || '–'}</Typography></Box>
              </Stack>
              {data.rating?.mijn_rating == null && data.aanwezigheid?.[mijnEmail] !== 'afwezig' && <Button size="small" onClick={() => setRatingLater(false)} sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}>Alsnog een cijfer geven</Button>}
            </Paper>
            <Box sx={{ display: 'grid', gridTemplateColumns: TWEE_KOLOMMEN, gap: 2 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 800, mb: 1 }}>Besluiten ({(data.besluiten || []).length})</Typography>
                <Stack spacing={0.75}>{(data.besluiten || []).map((bs) => <Box key={bs.id} sx={{ pl: 1.25, borderLeft: '3px solid #4338ca' }}><Typography sx={{ fontSize: 14, fontWeight: 700 }}>{bs.titel}</Typography><Typography sx={{ fontSize: 12, color: '#64748b' }}>{naam(bs.eigenaar_email)} · {bs.soort}{bs.rationale ? ` · ${bs.rationale}` : ''}</Typography></Box>)}{(data.besluiten || []).length === 0 && <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Geen.</Typography>}</Stack>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}><Typography sx={{ fontWeight: 800, mb: 1 }}>Nieuwe acties ({nieuweActies.length})</Typography><TodoLijst todos={nieuweActies} personen={personen} onToggle={toggleTodo} onBewerk={bewerkTodo} mijnEmail={mijnEmail} compact /></Paper>
              <Paper variant="outlined" sx={{ p: 2 }}><Typography sx={{ fontWeight: 800, mb: 1 }}>Headlines</Typography><HeadlineLijst headlines={data.headlines} magSchrijven={false} /></Paper>
              <Paper variant="outlined" sx={{ p: 2 }}><Typography sx={{ fontWeight: 800, mb: 1 }}>Nog open op de lijst</Typography><IssueLijst issues={data.issues} magSchrijven={false} onOpen={openIssue} context={issueContext} /></Paper>
            </Box>
            {s.notities && <Paper variant="outlined" sx={{ p: 2 }}><Typography sx={{ fontWeight: 800, mb: 0.5 }}>Notities</Typography><Typography sx={{ fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{s.notities}</Typography></Paper>}
          </Stack>
        )}
      </Box>

      <IssueContextPaneel issueId={issueOpen} typeId={t.id} onClose={sluitIssue} onGewijzigd={issueGewijzigd} onStatus={magSchrijven ? issueStatus : undefined} />
      <BesluitDialog open={besluitOpen} onClose={() => setBesluitOpen(false)} onOpslaan={nieuwBesluit} personen={personen} mijnEmail={mijnEmail || t.voorzitter_email} issue={besluitIssue} />
      <RatingDialog open={!!toonRating} mijnRating={data.rating?.mijn_rating} onKies={geefRating} onLater={() => setRatingLater(true)} />
    </Box>
  );
}
